<template>
    <div class="videocut-management-page">
      <div class="settings-card">
        <h2 class="page-title">
          <Icon icon="heroicons:scissors" width="24" height="24" />
          视频剪辑任务管理
        </h2>
  
        <!-- 工具条 -->
        <div class="toolbar">
          <div class="toolbar-left">
            <el-button round @click="fetchList" :loading="loading">
              <Icon icon="heroicons:arrow-path" width="16" height="16" />
              刷新
            </el-button>
  
            <el-input v-model="search.keyword" placeholder="按素材名/文案分组/成品分组/错误信息 搜索" clearable style="width: 360px"
              @keyup.enter="doSearch" class="ml10">
              <template #prefix>
                <Icon icon="heroicons:magnifying-glass" width="16" height="16" />
              </template>
            </el-input>
  
            <el-select v-model="search.status" placeholder="状态筛选" clearable style="width: 160px" class="ml10"
              @change="doSearch">
              <el-option :value="1" label="正常" />
              <el-option :value="2" label="失败" />
            </el-select>
  
            <el-button type="primary" class="ml10" @click="doSearch">
              <Icon icon="heroicons:magnifying-glass" width="16" height="16" />
              搜索
            </el-button>
  
            <el-popconfirm v-if="selectedIds.length" :title="`确认删除选中的 ${selectedIds.length} 条记录吗？`"
              @confirm="doBatchDelete">
              <template #reference>
                <el-button type="danger" class="ml10" :disabled="!selectedIds.length" :loading="batchDeleting">
                  批量删除<span v-if="selectedIds.length">（{{ selectedIds.length }}）</span>
                </el-button>
              </template>
            </el-popconfirm>
          </div>
  
          <div class="toolbar-right">
            <el-button type="success" round @click="openAdd">
              <Icon icon="heroicons:plus" width="16" height="16" />
              添加
            </el-button>
          </div>
        </div>
  
        <!-- 表格包装器 -->
        <div class="table-wrapper">
          <el-table :data="list" border stripe v-loading="loading" class="videocut-table"
            :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
            :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }"
            @selection-change="handleSelectionChange">
            <el-table-column type="selection" width="48" />
            <el-table-column prop="id" label="ID" width="80" />
  
            <el-table-column label="开始素材" min-width="160" show-overflow-tooltip>
              <template #default="s">{{ scName(s.row.video_start_id) || '—' }}</template>
            </el-table-column>
            <el-table-column label="中间素材" min-width="160" show-overflow-tooltip>
              <template #default="s">{{ scName(s.row.video_middle_id) || '—' }}</template>
            </el-table-column>
            <el-table-column label="结束素材" min-width="160" show-overflow-tooltip>
              <template #default="s">{{ scName(s.row.video_end_id) || '—' }}</template>
            </el-table-column>
            <el-table-column label="文案分组" min-width="160" show-overflow-tooltip>
              <template #default="s">{{ wenanName(s.row.wenan_id) || '—' }}</template>
            </el-table-column>
            <el-table-column label="成品分组" min-width="160" show-overflow-tooltip>
              <template #default="s">{{ cpName(s.row.video_cp_id) || '—' }}</template>
            </el-table-column>
  
            <el-table-column prop="jianji_num" label="剪辑数量" width="110" />
            <el-table-column label="状态" width="110">
              <template #default="s">
                <el-tag :type="s.row.status === 1 ? 'success' : 'danger'">
                  {{ s.row.status === 1 ? '正常' : '失败' }}
                </el-tag>
              </template>
            </el-table-column>
  
            <el-table-column prop="error_message" label="错误信息" min-width="200" show-overflow-tooltip />
            <el-table-column prop="create_time" label="创建时间" width="180" />
            <el-table-column prop="update_time" label="更新时间" width="180" />
  
            <el-table-column label="操作" width="200" fixed="right">
              <template #default="s">
                <button class="action-btn copy-btn" @click="copyRow(s.row)">
                  <Icon icon="heroicons:document-duplicate" width="14" height="14" />
                  复制
                </button>
                <el-popconfirm title="确认删除该记录吗？" @confirm="delOne(s.row.id)">
                  <template #reference>
                    <button class="action-btn delete-btn">
                      <Icon icon="heroicons:trash" width="14" height="14" />
                      删除
                    </button>
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </div>
  
        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination v-model:current-page="pager.page" v-model:page-size="pager.pageSize" :total="pager.total"
            :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper" @size-change="fetchList"
            @current-change="fetchList" />
        </div>
      </div>
  
      <!-- 新增弹窗 -->
      <el-dialog v-model="dialogVisible" title="新增短视频剪辑任务" width="720px" :close-on-click-modal="false"
        destroy-on-close class="custom-dialog">
        <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
          <el-form-item label="开始素材" prop="video_start_id">
            <el-select v-model="form.video_start_id" filterable clearable placeholder="请选择开始素材"
              style="width: 100%" :loading="loadingSources.sc">
              <el-option v-for="it in sources.sc" :key="`sc-start-${it.id}`" :label="it.video_name"
                :value="String(it.id)" />
            </el-select>
          </el-form-item>
  
          <el-form-item label="中间素材" prop="video_middle_id">
            <el-select v-model="form.video_middle_id" filterable clearable placeholder="请选择中间素材"
              style="width: 100%" :loading="loadingSources.sc">
              <el-option v-for="it in sources.sc" :key="`sc-middle-${it.id}`" :label="it.video_name"
                :value="String(it.id)" />
            </el-select>
          </el-form-item>
  
          <el-form-item label="结束素材" prop="video_end_id">
            <el-select v-model="form.video_end_id" filterable clearable placeholder="请选择结束素材"
              style="width: 100%" :loading="loadingSources.sc">
              <el-option v-for="it in sources.sc" :key="`sc-end-${it.id}`" :label="it.video_name"
                :value="String(it.id)" />
            </el-select>
          </el-form-item>
  
          <el-form-item label="文案分组" prop="wenan_id">
            <el-select v-model="form.wenan_id" filterable clearable placeholder="请选择文案分组" style="width: 100%"
              :loading="loadingSources.wenan">
              <el-option v-for="it in sources.wenan" :key="`wn-${it.id}`" :label="it.wenan_name"
                :value="String(it.id)" />
            </el-select>
          </el-form-item>
  
          <el-form-item label="成品分组" prop="video_cp_id">
            <el-select v-model="form.video_cp_id" filterable clearable placeholder="请选择视频成品分组"
              style="width: 100%" :loading="loadingSources.cp">
              <el-option v-for="it in sources.cp" :key="`cp-${it.id}`" :label="it.video_name"
                :value="String(it.id)" />
            </el-select>
          </el-form-item>
  
          <el-form-item label="剪辑数量" prop="jianji_num">
            <el-input v-model.number="form.jianji_num" type="number" :min="1" placeholder="请输入剪辑数量（整数）" />
          </el-form-item>
  
          <el-form-item label="状态" prop="status">
            <el-radio-group v-model="form.status">
              <el-radio :value="1">正常</el-radio>
              <el-radio :value="2">失败</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
  
        <template #footer>
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" :loading="submitting" @click="submit">提交</el-button>
        </template>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import axios from 'axios'
  import Cookies from 'js-cookie'
  import config from '@/config.js'
  import { Icon } from '@iconify/vue'
  
  export default {
    name: 'VideoCutTask',
    components: { Icon },
    data() {
      return {
        loading: false,
        batchDeleting: false,
        list: [],
        pager: { page: 1, pageSize: 10, total: 0 },
        search: { keyword: '', status: '' },
        selectedIds: [],
        dialogVisible: false,
        submitting: false,
        sources: {
          sc: [],
          wenan: [],
          cp: []
        },
        loadingSources: {
          sc: false,
          wenan: false,
          cp: false
        },
        form: {
          video_start_id: '',
          video_middle_id: '',
          video_end_id: '',
          wenan_id: '',
          video_cp_id: '',
          jianji_num: 1,
          status: 1
        },
        rules: {
          jianji_num: [{ required: true, type: 'number', message: '请输入剪辑数量', trigger: 'blur' }],
          status: [{ required: true, type: 'number', message: '请选择状态', trigger: 'change' }]
        }
      }
    },
    computed: {
      userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
      apiBase() { return `${config.BASE_URL}/shop/api` },
    },
    mounted() {
      this.fetchSources()
      this.fetchList()
    },
    methods: {
      async fetchList() {
        const action = (this.search.keyword || this.search.status !== '') ? 'search' : 'list'
        try {
          this.loading = true
          const payload = {
            action,
            store_id: this.userInfo.id,
            page: this.pager.page,
            page_size: this.pager.pageSize
          }
          if (this.search.keyword) payload.keyword = this.search.keyword
          if (this.search.status !== '') payload.status = this.search.status
          const { data } = await axios.post(`${this.apiBase}/video_cut_task.php`, payload)
          this.list = data.data || []
          this.pager.total = data.pagination ? data.pagination.total : (this.list?.length || 0)
          if (!this.list.length && this.pager.page > 1) {
            this.pager.page = this.pager.page - 1
            await this.fetchList()
          }
          this.selectedIds = []
        } catch (e) {
          console.error(e)
          this.$message.error('获取列表失败')
        } finally {
          this.loading = false
        }
      },
      doSearch() {
        this.pager.page = 1
        this.fetchList()
      },
      handleSelectionChange(rows) {
        this.selectedIds = rows.map(r => r.id)
      },
      async delOne(id) {
        try {
          const { data } = await axios.post(`${this.apiBase}/video_cut_task.php`, {
            action: 'delete',
            id,
            store_id: this.userInfo.id
          })
          if (data.success) {
            this.$message.success('已删除')
            this.fetchList()
          } else {
            this.$message.error(data.error || '删除失败')
          }
        } catch (e) {
          this.$message.error('删除失败')
        }
      },
      async doBatchDelete() {
        if (!this.selectedIds.length) return
        try {
          this.batchDeleting = true
          const { data } = await axios.post(`${this.apiBase}/video_cut_task.php`, {
            action: 'batch_delete',
            store_id: this.userInfo.id,
            ids: this.selectedIds
          })
          if (data.success) {
            this.$message.success('批量删除成功')
            await this.fetchList()
          } else {
            this.$message.error(data.error || '批量删除失败')
          }
        } catch {
          this.$message.error('批量删除失败')
        } finally {
          this.batchDeleting = false
        }
      },
      openAdd() {
        this.dialogVisible = true
        this.$nextTick(() => this.$refs.formRef?.clearValidate?.())
        this.form = {
          video_start_id: '',
          video_middle_id: '',
          video_end_id: '',
          wenan_id: '',
          video_cp_id: '',
          jianji_num: 1,
          status: 1
        }
      },
      submit() {
        this.$refs.formRef.validate(async (ok) => {
          if (!ok) return
          try {
            this.submitting = true
            const payload = {
              action: 'add',
              store_id: this.userInfo.id,
              video_start_id: this.form.video_start_id || null,
              video_middle_id: this.form.video_middle_id || null,
              video_end_id: this.form.video_end_id || null,
              wenan_id: this.form.wenan_id || null,
              video_cp_id: this.form.video_cp_id || null,
              jianji_num: Number(this.form.jianji_num) || 1,
              status: Number(this.form.status) || 1
            }
            const { data } = await axios.post(`${this.apiBase}/video_cut_task.php`, payload)
            if (data.success) {
              this.$message.success('新增成功')
              this.dialogVisible = false
              this.fetchList()
            } else {
              this.$message.error(data.error || '新增失败')
            }
          } catch (e) {
            console.error(e)
            this.$message.error('新增失败')
          } finally {
            this.submitting = false
          }
        })
      },
      scName(id) {
        if (!id) return ''
        const item = this.sources.sc.find(x => String(x.id) === String(id))
        return item ? item.video_name : ''
      },
      wenanName(id) {
        if (!id) return ''
        const item = this.sources.wenan.find(x => String(x.id) === String(id))
        return item ? item.wenan_name : ''
      },
      cpName(id) {
        if (!id) return ''
        const item = this.sources.cp.find(x => String(x.id) === String(id))
        return item ? item.video_name : ''
      },
      copyRow(row) {
        this.dialogVisible = true
        this.$nextTick(() => this.$refs.formRef?.clearValidate?.())
        this.form = {
          video_start_id: row.video_start_id || '',
          video_middle_id: row.video_middle_id || '',
          video_end_id: row.video_end_id || '',
          wenan_id: row.wenan_id || '',
          video_cp_id: row.video_cp_id || '',
          jianji_num: row.jianji_num || 1,
          status: row.status || 1
        }
      },
      async fetchSources() {
        await Promise.all([
          this.fetchSC(),
          this.fetchWenan(),
          this.fetchCP()
        ])
      },
      async fetchSC() {
        try {
          this.loadingSources.sc = true
          const { data } = await axios.post(`${this.apiBase}/video_sc_management.php`, {
            action: 'list_all',
            store_id: this.userInfo.id
          })
          const rows = data?.data || []
          this.sources.sc = rows.map(r => ({ ...r, id: r.id }))
        } catch (e) {
          this.sources.sc = []
        } finally {
          this.loadingSources.sc = false
        }
      },
      async fetchWenan() {
        try {
          this.loadingSources.wenan = true
          const { data } = await axios.post(`${this.apiBase}/wenan_management.php`, {
            action: 'list_all',
            store_id: this.userInfo.id
          })
          const rows = data?.data || []
          this.sources.wenan = rows.map(r => ({ ...r, id: r.id }))
        } catch (e) {
          this.sources.wenan = []
        } finally {
          this.loadingSources.wenan = false
        }
      },
      async fetchCP() {
        try {
          this.loadingSources.cp = true
          const { data } = await axios.post(`${this.apiBase}/video_cp_management.php`, {
            action: 'list_all',
            store_id: this.userInfo.id
          })
          const rows = data?.data || []
          this.sources.cp = rows.map(r => ({ ...r, id: r.id }))
        } catch (e) {
          this.sources.cp = []
        } finally {
          this.loadingSources.cp = false
        }
      }
    }
  }
  </script>
  
  <style scoped lang="scss">
  .videocut-management-page {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
  }
  
  .settings-card {
    background: rgba(18, 14, 33, 0.6);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(157, 78, 221, 0.2);
    padding: 24px;
    width: 100%;
    box-sizing: border-box;
  
    @media (max-width: 768px) {
      padding: 16px;
    }
  
    @media (max-width: 576px) {
      padding: 12px;
    }
  }
  
  .page-title {
    font-size: 20px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 10px;
  
    svg {
      color: #c084fc;
    }
  
    @media (max-width: 768px) {
      font-size: 18px;
      margin-bottom: 16px;
    }
  }
  
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }
  
  .toolbar-left,
  .toolbar-right {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .ml10 {
    margin-left: 10px;
  }
  
  .table-wrapper {
    position: relative;
    width: 100%;
    overflow-x: auto;
    margin-top: 20px;
    -webkit-overflow-scrolling: touch;
  
    &::-webkit-scrollbar {
      height: 8px;
    }
  
    &::-webkit-scrollbar-track {
      background: rgba(157, 78, 221, 0.1);
      border-radius: 4px;
    }
  
    &::-webkit-scrollbar-thumb {
      background: rgba(157, 78, 221, 0.4);
      border-radius: 4px;
  
      &:hover {
        background: rgba(157, 78, 221, 0.6);
      }
    }
  }
  
  :deep(.el-table) {
    --el-table-bg-color: transparent !important;
    --el-table-tr-bg-color: transparent !important;
    --el-table-header-bg-color: transparent !important;
    --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
    --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
    --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
    --el-table-border-color: rgba(157, 78, 221, 0.2) !important;
  
    background-color: transparent !important;
    color: #e0e0e0;
  
    &::before {
      display: none;
    }
  
    .el-table__header-wrapper {
      background-color: transparent !important;
  
      tr {
        background-color: transparent !important;
  
        th:not(.el-table-fixed-column--right) {
          background: rgba(157, 78, 221, 0.2) !important;
          color: #fff !important;
          font-weight: 600;
          border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;
  
          .cell {
            color: #fff !important;
          }
        }
      }
    }
  
    .el-table__body-wrapper {
      background-color: transparent !important;
  
      tr {
        background-color: transparent !important;
  
        td:not(.el-table-fixed-column--right) {
          background-color: transparent !important;
          border-bottom: 1px solid rgba(157, 78, 221, 0.1);
          color: #e0e0e0;
        }
  
        &.el-table__row--striped td:not(.el-table-fixed-column--right) {
          background-color: rgba(157, 78, 221, 0.05) !important;
        }
  
        &:hover td:not(.el-table-fixed-column--right) {
          background-color: rgba(157, 78, 221, 0.1) !important;
        }
      }
    }
  
    .el-table-fixed-column--right {
      background-color: #120e21 !important;
      backdrop-filter: none !important;
    }
  
    th.el-table-fixed-column--right {
      background-color: #2a1f45 !important;
    }
  
    .el-table__row--striped .el-table-fixed-column--right {
      background-color: #1a1430 !important;
    }
  
    .el-table__empty-block {
      background-color: transparent !important;
  
      .el-table__empty-text {
        color: #a0a0c0 !important;
      }
    }
  }
  
  .action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    margin: 0 4px;
  
    &.copy-btn {
      background: rgba(59, 130, 246, 0.15);
      border: 1px solid rgba(59, 130, 246, 0.4);
      color: #60a5fa;
  
      &:hover {
        background: rgba(59, 130, 246, 0.25);
      }
    }
  
    &.delete-btn {
      background: rgba(239, 68, 68, 0.15);
      border: 1px solid rgba(239, 68, 68, 0.4);
      color: #f87171;
  
      &:hover {
        background: rgba(239, 68, 68, 0.25);
      }
    }
  }
  
  .pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
  
    @media (max-width: 768px) {
      justify-content: center;
    }
  }
  
  :deep(.el-pagination) {
    --el-pagination-bg-color: transparent;
    --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
    --el-pagination-hover-color: #c084fc;
  
    .btn-prev,
    .btn-next,
    .el-pager li {
      background: rgba(157, 78, 221, 0.1);
      border: 1px solid rgba(157, 78, 221, 0.3);
      color: #a0a0c0;
      border-radius: 8px;
      min-width: 32px;
      height: 32px;
  
      &:hover {
        background: rgba(157, 78, 221, 0.2);
        color: #c084fc;
      }
  
      &.active {
        background: linear-gradient(135deg, #6546b4, #9d4edd);
        color: #fff;
        border-color: transparent;
      }
    }
  
    .el-pagination__total,
    .el-pagination__jump {
      color: #a0a0c0;
    }
  }
  
  :deep(.custom-dialog) {
    --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
    --el-dialog-border-radius: 20px;
  
    .el-dialog__header {
      border-bottom: 1px solid rgba(157, 78, 221, 0.2);
      padding: 20px 24px;
  
      .el-dialog__title {
        color: #fff;
        font-weight: 600;
      }
    }
  
    .el-dialog__body {
      padding: 24px;
    }
  
    .el-dialog__footer {
      border-top: 1px solid rgba(157, 78, 221, 0.2);
      padding: 16px 24px;
    }
  }
  
  :deep(.el-form-item__label) {
    color: #a0a0c0;
    font-weight: 500;
  }
  
  :deep(.el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    box-shadow: none;
  
    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }
  
    &.is-focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }
  
    .el-input__inner {
      color: #fff;
      background: transparent;
  
      &::placeholder {
        color: #5a5a7a;
      }
    }
  }
  
  :deep(.el-button) {
    border-radius: 10px;
    padding: 10px 20px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
  }
  
  :deep(.el-button--primary) {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    border: none;
  
    &:hover {
      transform: translateY(-1px);
    }
  }
  
  :deep(.el-button--success) {
    background: linear-gradient(135deg, #10b981, #34d399);
    border: none;
  
    &:hover {
      transform: translateY(-1px);
    }
  }
  </style>
  
  <!-- 非 scoped 全局样式 -->
  <style lang="scss">
  .el-dialog {
    --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
  }
  
  body>.el-overlay:has(.custom-dialog) .el-dialog__body {
    background: rgba(18, 14, 33, 0.98) !important;
  }
  </style>